/**
 * EAI-1889 — "sticky" applied filters.
 *
 * The filters a user currently has applied are saved to the server as a
 * per-user + per-location preference and re-applied on the next sign-in, so
 * they survive logout (which purges redux-persist) and a page reload (the
 * opportunity slice isn't persisted at all).
 *
 * This is NOT the named preset/saved-filter feature. Presets are created by
 * hand, named, listed and applied deliberately. These records are implicit:
 * written on every filter change, read once per page load, and never shown in
 * any list.
 *
 * Storage reuses the generic filter store — `generic_filter` is already keyed
 * by (user_id, location_id, filter_name, generic_filter_type_id), which is
 * exactly the scope we need, so no new table and no backend change.
 *
 * Two constraints this module exists to encode:
 *
 * 1. The reserved names below are WEB-ONLY. Mobile keeps its own record named
 *    `LEAD_FILTER_ACTIVITY_CENTER` whose `filterJson` is a bare ARRAY of
 *    engagement-status codes (see smai-mobile-app `Home.js`, which JSON.parses
 *    it and hands it straight to `replaceQuickFilter`). Writing an object into
 *    that record would push an object into the mobile app's
 *    `engagementStatusFilters` and break its lead list, so we never touch it.
 *    The mobile follow-up ticket migrates both surfaces onto shared names.
 *
 * 2. Records are written with `isCurrent: false`. We find ours by name, and
 *    `isCurrent: true` makes the backend demote `is_current` on every other
 *    record of the same (user, location, type) — which the preset dropdowns
 *    round-trip.
 */

export const STICKY_ENVELOPE_VERSION = 1;

/**
 * Module registry. `mode` feeds the existing `getGenericFilterId(types, mode)`
 * helper in `src/library/utils.js` (0 = LEAD, 1 = LEAD_OPPORTUNITY,
 * 2 = LEAD_FILTER_ACTIVITY_CENTER).
 */
export const STICKY_MODULES = {
  ACTIVITY_CENTER: {
    key: "ACTIVITY_CENTER",
    mode: 2,
    filterName: "WEB_ACTIVITY_CENTER_CURRENT_FILTER",
  },
  OPPORTUNITY: {
    key: "OPPORTUNITY",
    mode: 1,
    filterName: "WEB_LEAD_OPPORTUNITY_CURRENT_FILTER",
  },
};

/**
 * Every filter name that is machine-owned rather than user-owned. Preset lists
 * must exclude all of these: ours because they are implicit state, and
 * mobile's because it is another surface's internal record.
 */
export const RESERVED_FILTER_NAMES = [
  STICKY_MODULES.ACTIVITY_CENTER.filterName,
  STICKY_MODULES.OPPORTUNITY.filterName,
  // smai-mobile-app's current-state record (array-shaped payload).
  "LEAD_FILTER_ACTIVITY_CENTER",
];

export const isReservedFilterName = name =>
  RESERVED_FILTER_NAMES.some(
    reserved => (name || "").trim().toUpperCase() === reserved.toUpperCase()
  );

/**
 * Activity Center keys we persist. Everything else in `state.lead.filters` is
 * either derived (the `*FilterCount` values, recomputed on restore) or
 * deliberately excluded — see `pickStickyFilters`.
 *
 * `leadSort` and `searchAllLeads` live at the root of the lead slice, not
 * inside `filters`; callers merge them in before picking.
 */
export const ACTIVITY_CENTER_FILTER_KEYS = [
  "quickFilters",
  "addMyConversation",
  "campaignFilters",
  "userFilters",
  "teamFilters",
  "dateFilterType",
  "dateRange",
  "dateType",
  "opportunityTypeFilters",
  "opportunityStatusFilters",
  "opportunitySourceFilters",
  // The three chip counts are stored even though they are derived from the id
  // arrays above, because the derivation is NOT reproducible at restore time:
  // `computeParentCount` counts only top-level selections and needs the
  // opportunity type / status / source TREES, which load asynchronously and
  // only when the user opens that category. Recomputing later would leave the
  // panel with no badge — and, worse, "Clear all" and "Save filter" are gated
  // on these counts, so a restored filter would be unclearable from the panel.
  "opportunityTypeFilterCount",
  "opportunityStatusFilterCount",
  "opportunitySourceFilterCount",
  "leadSort",
  "searchAllLeads",
];

/** Opportunity-view keys we persist, from `opportunitySlice` initialFilters. */
export const OPPORTUNITY_FILTER_KEYS = [
  "opportunityType",
  "opportunityStatus",
  "opportunitySource",
  "opportunityVOI",
  "userTypesOr",
  "userTypesAnd",
  "ownerIds",
  "assignedUserIds",
  "teamIds",
  "entityMode",
  "activeFilterMode",
  "viewMode",
  "filterType",
  "dateRange",
  "dateType",
];

const KEYS_BY_MODULE = {
  [STICKY_MODULES.ACTIVITY_CENTER.key]: ACTIVITY_CENTER_FILTER_KEYS,
  [STICKY_MODULES.OPPORTUNITY.key]: OPPORTUNITY_FILTER_KEYS,
};

/**
 * Reduce a live filter object to the keys we store, in a FIXED key order so
 * two equal filter sets always serialize to the same string (the change
 * detection compares serialized payloads).
 *
 * Keys the source doesn't carry are omitted rather than written as null, so a
 * caller that hasn't loaded a slice yet can't blank a stored value.
 *
 * Never included, whatever the caller passes: free-text search, paging, the
 * derived counts, `cameFromDashboard`, `appliedFilterSnapshot`, selected lead.
 */
export const pickStickyFilters = (moduleKey, source) => {
  const keys = KEYS_BY_MODULE[moduleKey];
  if (!keys || !source || typeof source !== "object") return {};
  const picked = {};
  keys.forEach(key => {
    const value = source[key];
    if (value === undefined) return;
    picked[key] = value;
  });
  return picked;
};

/** Wrap a picked filter set for storage. */
export const encodeStickyEnvelope = filters => ({
  v: STICKY_ENVELOPE_VERSION,
  source: "web",
  updatedAt: new Date().toISOString(),
  filters: filters || {},
});

/**
 * Read a stored `filterJson` string back into a filter object.
 *
 * Tolerates three shapes and returns `null` for anything it can't use, so a
 * malformed or foreign record degrades to "no stored state" instead of
 * corrupting the filter shape:
 *   - a v1 envelope        → its `filters`
 *   - a bare object        → itself (older web-written records / preset shape)
 *   - an array, or garbage → null (mobile's status-array record)
 */
export const decodeStickyEnvelope = filterJson => {
  if (!filterJson || typeof filterJson !== "string") return null;
  let parsed;
  try {
    parsed = JSON.parse(filterJson);
  } catch (err) {
    return null;
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
  if (parsed.v === STICKY_ENVELOPE_VERSION) {
    const { filters } = parsed;
    if (!filters || typeof filters !== "object" || Array.isArray(filters)) return null;
    return filters;
  }
  // An unknown future version: don't guess at its shape.
  if (parsed.v !== undefined) return null;
  return parsed;
};

/** Stable comparison of two picked filter sets. */
export const stickyFiltersEqual = (a, b) => JSON.stringify(a || {}) === JSON.stringify(b || {});

/** The quick filter some users' permissions force on. */
export const MY_CONVERSATIONS = "MY_CONVERSATIONS";

/**
 * Turn a stored Activity Center payload into what the lead slice needs.
 *
 * Pure and exported so the rules below are testable, in particular the
 * My Conversations ones — that filter is permission-driven and must behave
 * exactly as it did before sticky filters existed.
 *
 * The rules:
 *
 * 1. `leadSort` / `searchAllLeads` are returned separately. They live at the
 *    ROOT of the lead slice, and `applyLeadFilters` would bury them inside
 *    `filters`, where nothing reads them.
 * 2. A category with ids but no count gets a provisional count of 1, so the
 *    badges and the Clear all / Save filter gating work before that category's
 *    tree has loaded. The panel reconciles it to the exact number later.
 * 3. Under `restrictRemoveDefaultFilter`, MY_CONVERSATIONS is re-applied from
 *    the CURRENT user's permissions. A stored set that predates the permission
 *    — or was saved by a user who could remove it — must never leave a
 *    restricted user unrestricted.
 * 4. `addMyConversation` is passed through untouched. It is not a filter: it is
 *    the one-shot "auto-apply My Conversations for a non-team-leader" flag that
 *    `setFilterStatusForTeamLeader` reads, and the slice clears it as soon as
 *    the user takes manual control of the quick filters. Preserving it is what
 *    stops a user's deliberate "off" from being auto-re-added on the next
 *    sign-in; forcing it either way would change behaviour that predates this
 *    feature.
 */
export const prepareRestoredLeadFilters = (stored, { restrictRemoveDefaultFilter } = {}) => {
  if (!stored || typeof stored !== "object" || Array.isArray(stored)) return null;

  const { leadSort, searchAllLeads, ...filters } = stored;

  [
    ["opportunityTypeFilters", "opportunityTypeFilterCount"],
    ["opportunityStatusFilters", "opportunityStatusFilterCount"],
    ["opportunitySourceFilters", "opportunitySourceFilterCount"],
  ].forEach(([idsKey, countKey]) => {
    const ids = Array.isArray(filters[idsKey]) ? filters[idsKey] : [];
    if (ids.length > 0 && !(filters[countKey] > 0)) filters[countKey] = 1;
  });

  if (restrictRemoveDefaultFilter) {
    const quick = Array.isArray(filters.quickFilters) ? filters.quickFilters : [];
    if (!quick.includes(MY_CONVERSATIONS)) filters.quickFilters = [...quick, MY_CONVERSATIONS];
  }

  return {
    filters,
    leadSort: typeof leadSort === "string" && leadSort ? leadSort : null,
    searchAllLeads: typeof searchAllLeads === "boolean" ? searchAllLeads : null,
  };
};

/**
 * The location these records are scoped to.
 *
 * `sessionStorage.currentLocationId` is the authoritative live value — the
 * redux location slice is null on a fresh Lead Center mount and lags in-tab
 * navigations (see `authenticator-provider.js`, where sLocationId is resolved
 * the same way).
 *
 * Returns null when the session holds no location, or holds several ("All
 * locations" is a comma-joined list). Null means "send no locationId", and the
 * backend then resolves the user's own location — so All-locations shares the
 * user's default-location record, which is the agreed behaviour for EAI-1889.
 */
export const getStickyLocationId = () => {
  if (typeof window === "undefined") return null;
  let raw;
  try {
    raw = sessionStorage.getItem("currentLocationId");
  } catch (err) {
    return null;
  }
  if (!raw || raw === "all") return null;
  if (String(raw).includes(",")) return null;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
};

/**
 * Impersonation is read-only for this feature: an admin acting as someone else
 * must not overwrite that person's stored filters.
 */
export const isImpersonating = () => {
  if (typeof window === "undefined") return false;
  try {
    return Boolean(sessionStorage.getItem("impersonateUserId"));
  } catch (err) {
    return false;
  }
};

const SESSION_NONCE_KEY = "stickyFiltersSessionId";

/**
 * An id for the current signed-in session, used in the cache key below.
 *
 * Logging out is a client-side `navigate`, not a page load, so the module-level
 * bookkeeping in `useStickyFilters` outlives the session that created it. That
 * made a sign-out → sign-in as the SAME user at the same location look like
 * "already synced": the stored set was never re-applied, and the save effect
 * then wrote the post-logout (empty) filters over the user's record.
 *
 * Logout calls `sessionStorage.clear()`, so this value disappears with the
 * session and the next sign-in gets a fresh key. It deliberately lives in
 * sessionStorage rather than a module variable for exactly that reason. A page
 * reload keeps the id, which is harmless — the module map is empty by then, so
 * the stored filters are read and applied regardless.
 */
export const getStickySessionNonce = () => {
  if (typeof window === "undefined") return "ssr";
  try {
    const existing = sessionStorage.getItem(SESSION_NONCE_KEY);
    if (existing) return existing;
    const nonce = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    sessionStorage.setItem(SESSION_NONCE_KEY, nonce);
    return nonce;
  } catch (err) {
    return "nosession";
  }
};

/**
 * Cache key for the "already synced" bookkeeping in `useStickyFilters`. Scoped
 * by user so a second sign-in on the same page load can't inherit the previous
 * user's bookkeeping, by location so a switch re-reads, and by session so a
 * re-login restores rather than being treated as already handled.
 */
export const stickyCacheKey = (moduleKey, userId, locationId, sessionNonce) =>
  `${moduleKey}:${userId ?? "anon"}:${locationId ?? "default"}:${sessionNonce ?? "nosession"}`;
